import { IPublicClientApplication, EventType, EventMessage } from "@azure/msal-browser";
import { apiConfig } from "../config/authConfig";

export class AuthMonitor {
  private msalInstance: IPublicClientApplication;
  private tokenCheckInterval: NodeJS.Timeout | null = null;
  private readonly CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
  private readonly RENEWAL_THRESHOLD = 10 * 60 * 1000; // Renew if expires within 10 minutes

  constructor(msalInstance: IPublicClientApplication) {
    this.msalInstance = msalInstance;
    this.setupEventHandlers();
  }

  private setupEventHandlers() {
    this.msalInstance.addEventCallback((event: EventMessage) => {
      switch (event.eventType) {
        case EventType.LOGIN_SUCCESS:
          console.log("Login successful, starting token monitoring...");
          this.startTokenMonitoring();
          break;
        
        case EventType.LOGOUT_SUCCESS:
          console.log("Logout successful, stopping token monitoring...");
          this.stopTokenMonitoring();
          break;
        
        case EventType.ACQUIRE_TOKEN_FAILURE:
          console.warn("Token acquisition failed:", event.error);
          // Do not immediately logout; allow guarded components to handle interactive login.
          break;
        
        case EventType.ACQUIRE_TOKEN_SUCCESS:
          console.log("Token acquired successfully");
          break;
      }
    });
  }

  private handleTokenFailure(error: any) {
    // Deprecated aggressive logout; retained for backward compatibility if needed.
  }

  public startTokenMonitoring() {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
    }

    this.tokenCheckInterval = setInterval(() => {
      this.checkAndRenewTokens();
    }, this.CHECK_INTERVAL);

    // Also check immediately
    this.checkAndRenewTokens();
  }

  public stopTokenMonitoring() {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }
  }

  private async checkAndRenewTokens() {
    const activeAccount = this.msalInstance.getActiveAccount();
    
    if (!activeAccount) {
      console.log("No active account found during token check");
      return;
    }

    try {
      const silentRequest = {
        scopes: apiConfig.scopes,
        account: activeAccount,
        forceRefresh: false,
      };

      // Try to get cached token info
      const allAccounts = this.msalInstance.getAllAccounts();
      
      if (allAccounts.length === 0) {
        console.log("No cached accounts found");
        return;
      }

      // Check if tokens are close to expiry
      const now = Date.now();
      const shouldRenew = await this.shouldRenewToken();
      if (shouldRenew) {
        console.log("Proactively renewing tokens...");
        try {
          await this.msalInstance.acquireTokenSilent({
            ...silentRequest,
            forceRefresh: true,
          });
          console.log("Token renewed successfully");
        } catch (renewError: any) {
          console.warn("Failed to renew token:", renewError);
        }
      }
    } catch (error) {
      console.error("Error during token check:", error);
    }
  }

  private async shouldRenewToken(): Promise<boolean> {
    const activeAccount = this.msalInstance.getActiveAccount();
    if (!activeAccount) return false;
    try {
      const silent = await this.msalInstance.acquireTokenSilent({
        scopes: apiConfig.scopes,
        account: activeAccount,
        forceRefresh: false,
      });
      if (!silent.expiresOn) return false;
      const timeLeft = silent.expiresOn.getTime() - Date.now();
      return timeLeft < this.RENEWAL_THRESHOLD;
    } catch {
      // If silent acquisition fails, let guarded components handle it
      return false;
    }
  }

  private forceLogout() {
    // Soft disable direct forced logout to reduce duplicate prompts.
    this.stopTokenMonitoring();
  }

  public destroy() {
    this.stopTokenMonitoring();
  }
} 